import { z } from 'zod';
import { ProfileAvatarDesign, RoleId } from '../members/index.js';
import { CalendarEventLocationType, CalendarMeetingProvider, } from '../calendar/event.js';
const Uuid = z.string().uuid();
const IsoDateTime = z.string().datetime({ offset: true });
const LocationFields = {
    locationType: CalendarEventLocationType.nullable().optional(),
    meetingProvider: CalendarMeetingProvider.nullable().optional(),
    meetingDetail: z.string().min(1).max(1000).nullable().optional(),
    locationText: z.string().min(1).max(500).nullable().optional(),
};
export const COORDINATION_POLL_LINKABLE_KIND = 'coordination-poll';
export const COORDINATION_SLOT_LINKABLE_KIND = 'coordination-slot';
export const CoordinationPollStatus = z.enum([
    'draft',
    'live',
    'closed',
    'committed',
    'archived',
]);
export const CoordinationResponsePolicy = z.enum(['strict_all', 'ignore_missing', 'threshold']);
export const CoordinationSlotStatus = z.enum([
    'candidate',
    'selected',
    'rejected',
    'committed',
    'cancelled',
]);
export const CoordinationAvailability = z.enum(['available', 'maybe', 'unavailable']);
export const CoordinationCommitMode = z.enum(['calendar_only', 'calendar_and_scheduling']);
export const CoordinationRoundInput = z.object({
    label: z.string().min(1).max(120),
    minSelectedSlots: z.number().int().min(0).max(20).optional(),
    maxSelectedSlots: z.number().int().min(1).max(20).optional(),
    required: z.boolean().optional(),
    slots: z.array(z.object({
        startsAt: IsoDateTime,
        endsAt: IsoDateTime,
        title: z.string().min(1).max(180),
        location: z.string().min(1).max(500).nullable().optional(),
        ...LocationFields,
        notes: z.string().min(1).max(2000).nullable().optional(),
        capacity: z.number().int().min(1).max(500).nullable().optional(),
    }).strict()).min(1).max(80),
}).strict();
export const CreateCoordinationPollRequest = z.object({
    title: z.string().min(1).max(180),
    description: z.string().max(2000).nullable().optional(),
    groupId: Uuid,
    groupIds: z.array(Uuid).min(1).max(20).optional(),
    optionalParticipantUserIds: z.array(Uuid).max(1000).optional(),
    surveyId: Uuid.nullable().optional(),
    tzid: z.string().min(1).max(64).optional(),
    responsePolicy: CoordinationResponsePolicy.optional(),
    thresholdCount: z.number().int().min(1).max(1000).nullable().optional(),
    thresholdPercent: z.number().int().min(1).max(100).nullable().optional(),
    rounds: z.array(CoordinationRoundInput).min(1).max(50),
}).strict();
export const UpdateCoordinationPollRequest = CreateCoordinationPollRequest.partial().extend({
    rounds: z.array(CoordinationRoundInput).min(1).max(50).optional(),
});
export const SubmitCoordinationResponseRequest = z.object({
    subjectUserId: Uuid.optional(),
    rounds: z.array(z.object({
        roundId: Uuid,
        note: z.string().min(1).max(1000).nullable().optional(),
        votes: z.array(z.object({
            slotId: Uuid,
            availability: CoordinationAvailability,
            priority: z.number().int().min(1).max(100).nullable().optional(),
        }).strict()).min(1).max(120),
    }).strict()).min(1).max(50),
}).strict();
export const CommitCoordinationPollRequest = z.object({
    slots: z.array(z.object({
        slotId: Uuid,
        commitMode: CoordinationCommitMode,
        schedulingHostSlug: z.string().min(3).max(80).optional(),
        ...LocationFields,
    }).strict()).min(1).max(120),
}).strict();
export const UpdateCoordinationSlotRequest = z.object({
    startsAt: IsoDateTime.optional(),
    endsAt: IsoDateTime.optional(),
    title: z.string().min(1).max(180).optional(),
    location: z.string().min(1).max(500).nullable().optional(),
    ...LocationFields,
    notes: z.string().min(1).max(2000).nullable().optional(),
    capacity: z.number().int().min(1).max(500).nullable().optional(),
}).strict();
export const CoordinationSlot = z.object({
    id: Uuid,
    pollId: Uuid,
    roundId: Uuid,
    position: z.number().int().nonnegative(),
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    title: z.string(),
    location: z.string().nullable(),
    locationType: CalendarEventLocationType.nullable(),
    meetingProvider: CalendarMeetingProvider.nullable(),
    meetingDetail: z.string().nullable(),
    locationText: z.string().nullable(),
    notes: z.string().nullable(),
    capacity: z.number().int().positive().nullable(),
    status: CoordinationSlotStatus,
    commitMode: CoordinationCommitMode.nullable(),
    calendarEventId: Uuid.nullable(),
    schedulingAvailabilityId: Uuid.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const CoordinationRound = z.object({
    id: Uuid,
    pollId: Uuid,
    position: z.number().int().nonnegative(),
    label: z.string(),
    minSelectedSlots: z.number().int().nonnegative(),
    maxSelectedSlots: z.number().int().positive(),
    required: z.boolean(),
    slots: z.array(CoordinationSlot),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const CoordinationIdentity = z.object({
    userId: Uuid,
    displayName: z.string(),
    primaryRole: RoleId.optional(),
    avatarDesign: ProfileAvatarDesign.optional(),
});
export const CoordinationParticipantSubjectKind = z.enum(['self', 'child']);
export const CoordinationParticipant = z.object({
    pollId: Uuid,
    userId: Uuid,
    identity: CoordinationIdentity.nullable(),
    role: z.enum(['required', 'optional']),
    status: z.enum(['invited', 'responded', 'no_response']),
    subjectKind: CoordinationParticipantSubjectKind.optional(),
    representativeUserIds: z.array(Uuid).optional(),
    representatives: z.array(CoordinationIdentity).optional(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const CoordinationAudienceSubject = z.object({
    userId: Uuid,
    identity: CoordinationIdentity.nullable(),
    role: z.enum(['required', 'optional']),
    subjectKind: CoordinationParticipantSubjectKind,
    representativeUserIds: z.array(Uuid),
    representatives: z.array(CoordinationIdentity),
});
export const CoordinationAudiencePreviewRequest = z.object({
    groupIds: z.array(Uuid).min(1).max(20),
    optionalParticipantUserIds: z.array(Uuid).max(1000).optional(),
}).strict();
export const CoordinationAudiencePreview = z.object({
    participants: z.array(CoordinationAudienceSubject),
    staffObservers: z.array(CoordinationIdentity),
});
export const CoordinationAudiencePreviewResponse = z.object({
    audience: CoordinationAudiencePreview,
});
export const CoordinationSlotVote = z.object({
    responseId: Uuid,
    slotId: Uuid,
    availability: CoordinationAvailability,
    priority: z.number().int().positive().nullable(),
});
export const CoordinationResponse = z.object({
    id: Uuid,
    pollId: Uuid,
    roundId: Uuid,
    userId: Uuid,
    submittedByUserId: Uuid,
    subjectIdentity: CoordinationIdentity.nullable(),
    submittedByIdentity: CoordinationIdentity.nullable(),
    note: z.string().nullable(),
    submittedAt: IsoDateTime,
    votes: z.array(CoordinationSlotVote),
});
export const CoordinationPoll = z.object({
    id: Uuid,
    title: z.string(),
    description: z.string().nullable(),
    ownerId: Uuid.nullable(),
    groupId: Uuid,
    groupIds: z.array(Uuid).min(1),
    optionalParticipantUserIds: z.array(Uuid),
    surveyId: Uuid.nullable(),
    tzid: z.string(),
    status: CoordinationPollStatus,
    responsePolicy: CoordinationResponsePolicy,
    thresholdCount: z.number().int().positive().nullable(),
    thresholdPercent: z.number().int().min(1).max(100).nullable(),
    viewerCanWrite: z.boolean().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    publishedAt: IsoDateTime.nullable(),
    closedAt: IsoDateTime.nullable(),
});
export const CoordinationPollDetail = CoordinationPoll.extend({
    rounds: z.array(CoordinationRound),
    participants: z.array(CoordinationParticipant),
    responses: z.array(CoordinationResponse),
});
export const CoordinationSlotResult = z.object({
    slotId: Uuid,
    roundId: Uuid,
    availableCount: z.number().int().nonnegative(),
    maybeCount: z.number().int().nonnegative(),
    unavailableCount: z.number().int().nonnegative(),
    noResponseCount: z.number().int().nonnegative(),
    optionalAvailableCount: z.number().int().nonnegative(),
    optionalMaybeCount: z.number().int().nonnegative(),
    optionalUnavailableCount: z.number().int().nonnegative(),
    strictIntersection: z.boolean(),
    maybeIntersection: z.boolean(),
    thresholdMatch: z.boolean(),
    score: z.number(),
    availableUserIds: z.array(Uuid),
    maybeUserIds: z.array(Uuid),
    unavailableUserIds: z.array(Uuid),
    noResponseUserIds: z.array(Uuid),
    optionalAvailableUserIds: z.array(Uuid),
    optionalMaybeUserIds: z.array(Uuid),
    optionalUnavailableUserIds: z.array(Uuid),
});
export const CoordinationRoundResult = z.object({
    roundId: Uuid,
    slotResults: z.array(CoordinationSlotResult),
});
export const CoordinationResults = z.object({
    pollId: Uuid,
    participantCount: z.number().int().nonnegative(),
    requiredParticipantCount: z.number().int().nonnegative(),
    optionalParticipantCount: z.number().int().nonnegative(),
    rounds: z.array(CoordinationRoundResult),
    coveredAllSelectedUserIds: z.array(Uuid),
    missingAtLeastOneSelectedUserIds: z.array(Uuid),
    missingEverySelectedUserIds: z.array(Uuid),
});
export const CoordinationPollResponse = z.object({ poll: CoordinationPollDetail });
export const CoordinationPollListResponse = z.object({
    polls: z.array(CoordinationPoll),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
});
export const CoordinationSubmitResponse = z.object({ responses: z.array(CoordinationResponse) });
export const CoordinationResultsResponse = z.object({ results: CoordinationResults });
export const CoordinationCommitResponse = z.object({ poll: CoordinationPollDetail });
export const COORDINATION_ERROR_CODES = {
    Disabled: 'COORDINATION_DISABLED',
    Unauthenticated: 'COORDINATION_UNAUTHENTICATED',
    PollNotFound: 'COORDINATION_POLL_NOT_FOUND',
    SlotNotFound: 'COORDINATION_SLOT_NOT_FOUND',
    Forbidden: 'COORDINATION_FORBIDDEN',
    InvalidState: 'COORDINATION_INVALID_STATE',
    InvalidTimeRange: 'COORDINATION_INVALID_TIME_RANGE',
    InvalidThreshold: 'COORDINATION_INVALID_THRESHOLD',
    InvalidVote: 'COORDINATION_INVALID_VOTE',
    SchedulingHostRequired: 'COORDINATION_SCHEDULING_HOST_REQUIRED',
};
export const COORDINATION_ACTIVITY_KINDS = {
    PollCreated: 'coordination.poll-created',
    PollUpdated: 'coordination.poll-updated',
    PollOpened: 'coordination.poll-opened',
    ResponseSubmitted: 'coordination.response-submitted',
    SlotCommitted: 'coordination.slot-committed',
    Unavailable: 'coordination.unavailable',
    ResponseNeeded: 'coordination.response-needed',
    ScheduleChanged: 'coordination.schedule-changed',
    SlotCancelled: 'coordination.slot-cancelled',
};
//# sourceMappingURL=coordination.js.map